/**
 * AvatarWithLightbox - Wrapper do SmartAvatar com lightbox de visualização
 * 
 * Ao clicar no avatar, abre um modal de visualização ampliada da imagem.
 * Só habilita o clique quando há uma imagem real (ignora fallback/iniciais).
 * 
 * @version 1.0.0
 */

import { useState, useMemo } from 'react';
import { SmartAvatar, type SmartAvatarProps } from '@/components/ui/smart-avatar';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { getStorageUrl } from '@/lib/storage';
import { is3DAvatarId, get3DAvatarById } from '@/lib/utils/avatar-3d';

interface AvatarWithLightboxProps extends Omit<SmartAvatarProps, 'onClick'> {
  /** Desabilitar abertura do lightbox */
  disableLightbox?: boolean;
}

export function AvatarWithLightbox({ disableLightbox = false, ...props }: AvatarWithLightboxProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Resolve a URL da imagem para o lightbox (mesma lógica do SmartAvatar)
  const resolvedUrl = useMemo(() => {
    if (!props.src) return null;
    if (is3DAvatarId(props.src)) return get3DAvatarById(props.src);
    return getStorageUrl(props.src) ?? null;
  }, [props.src]);

  // Só habilita clique quando há imagem real disponível
  const canOpen = !disableLightbox && !!resolvedUrl;

  return (
    <>
      <SmartAvatar
        {...props}
        onClick={canOpen ? () => setIsOpen(true) : undefined}
      />

      {/* Lightbox de visualização ampliada */}
      {canOpen && (
        <ImageLightbox
          open={isOpen}
          onClose={() => setIsOpen(false)}
          imageUrl={resolvedUrl}
          caption={props.name ?? props.alt}
        />
      )}
    </>
  );
}
